'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useTracking } from '@/analytics';
import { TRACKING } from '@/helpers/tracking';

import { SoundFx } from './components/audio';
import { draw } from './components/draw';
import {
  armReady,
  BASE_W,
  createInitialState,
  DESKTOP_H,
  GameState,
  GameStatus,
  LEVELS,
  LiveStats,
  MOBILE_H,
  MOBILE_ZOOM,
  RunSummary,
  setDims,
  startRun,
  step,
} from './engine';

const HS_KEY = 'solar-surge:highscore';
const HD_KEY = 'solar-surge:bestdistance';
const MOBILE_QUERY = '(max-width: 640px)';
const STATS_INTERVAL_S = 0.1; // throttle HUD updates to ~10Hz (the loop runs at 60fps)

const INITIAL_STATS: LiveStats = {
  batteries: 0,
  distanceM: 0,
  energyKwh: 0,
  level: LEVELS[0],
  multiplier: 1,
  powerKw: LEVELS[0].powerKw,
  storageKwh: 0,
};

const readNumber = (key: string): number => {
  if (typeof window === 'undefined') return 0;
  return Number(window.localStorage.getItem(key)) || 0;
};

const mediaIsMobile = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;

/**
 * Logical width/height for the current device. The buffer's aspect ratio always
 * equals the on-screen box's (no skew, any dpr). A portrait (mobile) box shrinks
 * the whole logical space by MOBILE_ZOOM for a zoomed-in view; desktop is 480x360.
 */
const measureDims = (canvas: HTMLCanvasElement): { h: number; w: number } => {
  const rect = canvas.getBoundingClientRect();
  const laidOut = rect.width > 0 && rect.height > 0;
  const portrait = laidOut ? rect.height > rect.width : mediaIsMobile();
  const w = portrait ? Math.round(BASE_W / MOBILE_ZOOM) : BASE_W;
  if (laidOut) {
    const h = Math.round(w * (rect.height / rect.width));
    return { h: Math.max(DESKTOP_H, Math.min(900, h)), w };
  }
  return { h: portrait ? Math.round(MOBILE_H / MOBILE_ZOOM) : DESKTOP_H, w };
};

const readStats = (state: GameState): LiveStats => ({
  batteries: state.batteriesCollected,
  distanceM: state.distance,
  energyKwh: state.energy,
  level: state.level,
  multiplier: state.comboMult,
  powerKw: state.level.powerKw,
  storageKwh: state.storageKwh,
});

const playEventSounds = (state: GameState, sfx: SoundFx): void => {
  state.events.forEach((event) => {
    if (event.type === 'point') sfx.point();
    else if (event.type === 'battery') sfx.battery(event.mult);
    else if (event.type === 'levelUp') sfx.levelUp();
    else if (event.type === 'milestone') sfx.milestone();
    else if (event.type === 'crash') sfx.crash();
  });
  state.events = [];
};

export const useSolarSurge = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stateRef = useRef<GameState | null>(null);
  const statusRef = useRef<GameStatus>('idle');
  const inputRef = useRef({ thrust: false });
  const sfxRef = useRef<SoundFx | null>(null);

  const [status, setStatus] = useState<GameStatus>('idle');
  const [stats, setStats] = useState<LiveStats>(INITIAL_STATS);
  const [summary, setSummary] = useState<RunSummary | null>(null);
  // Start at 0 so the server-rendered HTML matches the first client render; the stored
  // values are read from localStorage in the mount effect (client-only) to avoid a
  // hydration mismatch for returning players.
  const [highScore, setHighScore] = useState(0);
  const [bestDistance, setBestDistance] = useState(0);
  const [soundOn, setSoundOn] = useState(true);

  const { trackEvent } = useTracking();
  const trackRef = useRef(trackEvent);
  trackRef.current = trackEvent;

  const applyStatus = useCallback((next: GameStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const recordRun = useCallback((run: RunSummary) => {
    setSummary(run);
    trackRef.current(TRACKING.SOLAR_SURGE_COMPLETED_RUN, {
      energyKwh: Math.round(run.energyKwh),
      isHighScore: run.isHighScore,
      topLevel: run.topLevel,
    });
    if (run.isHighScore) {
      setHighScore(run.energyKwh);
      window.localStorage.setItem(HS_KEY, String(run.energyKwh));
    }
    setBestDistance((prev) => {
      if (run.distanceM <= prev) return prev;
      window.localStorage.setItem(HD_KEY, String(run.distanceM));
      return run.distanceM;
    });
  }, []);

  const start = useCallback(() => {
    const state = stateRef.current;
    if (!state) return;
    setSummary(null);
    startRun(state);
    applyStatus('running');
    sfxRef.current?.unlock();
    trackRef.current(TRACKING.SOLAR_SURGE_STARTED_GAME);
  }, [applyStatus]);

  // Arm a fresh run but wait in attract mode for the player's first input.
  const arm = useCallback(() => {
    const state = stateRef.current;
    if (!state) return;
    setSummary(null);
    armReady(state);
    applyStatus('ready');
  }, [applyStatus]);

  // Thrust for the on-screen fly button and pointer/keys: pressing from idle/ready
  // launches the run so the control never feels dead.
  const hold = useCallback(
    (on: boolean) => {
      if (on && (statusRef.current === 'ready' || statusRef.current === 'idle')) start();
      inputRef.current.thrust = on;
      if (on) sfxRef.current?.unlock();
    },
    [start]
  );

  const toggleSound = useCallback(() => {
    setSoundOn((prev) => {
      const next = !prev;
      if (sfxRef.current) sfxRef.current.enabled = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const sfx = new SoundFx();
    sfxRef.current = sfx;

    const dims = measureDims(canvas);
    const state = createInitialState({ h: dims.h, highScore: readNumber(HS_KEY), w: dims.w });
    stateRef.current = state;
    // Client-only reads: hydrate the displayed bests after mount (see the useState note).
    setHighScore(readNumber(HS_KEY));
    setBestDistance(readNumber(HD_KEY));

    const resize = () => {
      const next = measureDims(canvas);
      setDims(state, next.w, next.h);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = next.w * dpr;
      canvas.height = next.h * dpr;
      const context = canvas.getContext('2d');
      if (context) context.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    // Input: canvas tap / mouse and keyboard. The fly button is wired via `hold`.
    const down = () => {
      if (statusRef.current === 'ready') start();
      else inputRef.current.thrust = true;
      sfx.unlock();
    };
    const up = () => {
      inputRef.current.thrust = false;
    };
    const touchDown = (event: TouchEvent) => {
      event.preventDefault();
      down();
    };
    const onKey = (event: KeyboardEvent, on: boolean) => {
      if (event.code !== 'Space' && event.code !== 'ArrowUp') return;
      event.preventDefault();
      if (on && statusRef.current === 'ready') {
        start();
        return;
      }
      inputRef.current.thrust = on;
      if (on) sfx.unlock();
    };
    const keyDown = (event: KeyboardEvent) => onKey(event, true);
    const keyUp = (event: KeyboardEvent) => onKey(event, false);

    canvas.addEventListener('mousedown', down);
    window.addEventListener('mouseup', up);
    canvas.addEventListener('touchstart', touchDown, { passive: false });
    window.addEventListener('touchend', up);
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);

    let raf = 0;
    let last = performance.now();
    let statsAcc = 0;
    const frame = (now: number) => {
      const dt = Math.min((now - last) / 1000, 1 / 30);
      last = now;

      step(state, inputRef.current, dt);
      playEventSounds(state, sfx);

      const context = canvas.getContext('2d');
      if (context) draw(context, state);

      if (state.status !== statusRef.current) {
        applyStatus(state.status);
        if (state.status === 'over' && state.summary) recordRun(state.summary);
      }

      statsAcc += dt;
      if (statsAcc >= STATS_INTERVAL_S) {
        statsAcc = 0;
        setStats(readStats(state));
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      sfx.close();
      canvas.removeEventListener('mousedown', down);
      window.removeEventListener('mouseup', up);
      canvas.removeEventListener('touchstart', touchDown);
      window.removeEventListener('touchend', up);
      window.removeEventListener('keydown', keyDown);
      window.removeEventListener('keyup', keyUp);
    };
  }, [applyStatus, recordRun, start]);

  return { arm, bestDistance, canvasRef, highScore, hold, soundOn, start, stats, status, summary, toggleSound };
};
